// Port of the app's lift_history_model.dart: top set per workout day, grouped by
// exercise, from progress_workouts/{uid}/days.

/** Whether `weight` × `reps` beats `best` — heaviest weight, ties broken by reps. */
export function beats(best, weight, reps) {
  const w = weight || 0;
  if (w > best.weight) return true;
  return w === best.weight && reps > best.reps;
}

/** Epley estimate of the one-rep max, or null for an unweighted set. */
export function estimatedOneRepMax(weight, reps) {
  if (!weight || weight <= 0 || reps <= 0) return null;
  if (reps === 1) return weight;
  return weight * (1 + reps / 30);
}

const num = (v) => (typeof v === 'number' && Number.isFinite(v) ? v : null);

/** A day doc's exercise map entry → { weight, reps, sets } (top_set, or legacy flat fields). */
function topSetOf(ex) {
  const src = ex.top_set && typeof ex.top_set === 'object' ? ex.top_set : ex;
  const weight = num(src.weight);
  const reps = num(src.reps);
  const sets = Array.isArray(ex.sets)
    ? ex.sets
      .filter((s) => s && typeof s === 'object')
      .map((s) => ({ reps: Math.trunc(num(s.reps) || 0), weight: num(s.weight) }))
    : [];
  return { weight, reps: reps == null ? null : Math.trunc(reps), sets };
}

/**
 * id → { exerciseId, name, entries: [{ date, weight, reps, sets }] (oldest first),
 * weighted }. Days without a top set (ticked off on Home) and cardio blocks
 * (0-rep, unweighted) are skipped.
 */
export function liftHistories(progressDays) {
  const byExercise = {};
  const names = {};
  for (const day of progressDays) {
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(day.id);
    if (!m || !day.exercises || typeof day.exercises !== 'object') continue;
    const date = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
    for (const [id, ex] of Object.entries(day.exercises)) {
      if (!ex || typeof ex !== 'object') continue;
      const { weight, reps, sets } = topSetOf(ex);
      if (reps == null || (reps === 0 && (weight || 0) <= 0)) continue;
      if (typeof ex.name === 'string' && ex.name) names[id] = ex.name;
      (byExercise[id] ||= []).push({ key: day.id, date, weight, reps, sets });
    }
  }
  return Object.entries(byExercise).map(([id, entries]) => {
    entries.sort((a, b) => a.date - b.date);
    return { exerciseId: id, name: names[id] || null, entries, weighted: entries.some((e) => (e.weight || 0) > 0) };
  });
}

/** Value plotted for an entry: its weight, or its reps for bodyweight work. */
export const liftValue = (h, e) => (h.weighted ? e.weight || 0 : e.reps);

export function liftBest(h) {
  let best = { weight: 0, reps: 0 };
  for (const e of h.entries) if (beats(best, e.weight, e.reps)) best = { weight: e.weight || 0, reps: e.reps };
  return best;
}

export function liftBestOneRepMax(h) {
  let best = null;
  for (const e of h.entries) {
    const orm = estimatedOneRepMax(e.weight, e.reps);
    if (orm != null && (best == null || orm > best)) best = orm;
  }
  return best;
}

/** Indices of entries that beat every earlier one (the first doesn't count). */
export function liftRecordIndices(h) {
  const records = new Set();
  let bar = null;
  h.entries.forEach((e, i) => {
    if (bar == null || beats(bar, e.weight, e.reps)) {
      if (bar != null) records.add(i);
      bar = { weight: e.weight || 0, reps: e.reps };
    }
  });
  return records;
}

/** 92.5 → "92.5", 80 → "80". */
export const formatLiftWeight = (w) => (w === Math.round(w) ? w.toFixed(0) : w.toFixed(1));

/** "80 kg × 5", or "12 reps" for a set without weight. */
export const formatLiftSet = (t, weight, reps) =>
  (!weight || weight <= 0 ? `${reps} ${t('repsUnit')}` : `${formatLiftWeight(weight)} kg × ${reps}`);

/** Catalog name, then the name stored with the workout or program, then a prettified ID. */
export function liftDisplayName(exerciseId, { exercise, customName, upper = (s) => s.toUpperCase() } = {}) {
  if (exercise && exercise.name) return exercise.name;
  if (customName && customName.trim()) return customName.trim();
  return exerciseId
    .split('_')
    .map((w) => (w ? upper(w[0]) + w.slice(1) : ''))
    .join(' ');
}
